import React, { useState, useEffect, useContext } from 'react'
import { observer } from "mobx-react-lite";
import { Draggable } from 'react-beautiful-dnd';
import {
  ChevronDown, ChevronUp, ChatDots,
  Copy, Envelope, BoxArrowUpRight, PlusLg, Trash3, StarFill
} from 'react-bootstrap-icons'
import axiosService from '../../../../../utils/AxiosService';
import getUnicodeFlagIcon from 'country-flag-icons/unicode'
import ReachbirdApprovedBadge from '../../../../Shared/ReachbirdApprovedBadge'
import { formatNumber } from '../../../../../utils/Helper'
import { toastError, toastSuccess } from '../../../../FormComponents/Toasts'
import { useNavigate, useParams } from 'react-router-dom'
import PlatformButton from './PlatformButton'
import RemoveInfluencerModal from './InfluencerItemPart/RemoveInfluencerModal';
import SelectionStatusIcon from '../../../../Shared/SelectionStatusIcon'
import { CampaignContext } from '../../../../../contexts/CampaignContext';
import { isUgcCreatorTag } from '../../../../../utils/tagStyles';
import { useReadOnly } from '../../../../../contexts/ReadOnlyContext';

export default observer(function InfluencerItem({ auth, index }){
  const { id } = useParams()
  const readOnly = useReadOnly()
  const { campaignState, selectedInfluencerAuth } = useContext(CampaignContext)

  const [influencer, setInfluencer] = useState({});
  const [authentications, setAuthentications] = useState([])
  const [removeInfluencerModalOpen, setRemoveInfluencerModalOpen] = useState(false)

  const getInfluencer = () => {
    axiosService.get(`/api/project_manager/authentications/${auth.authentication_id}/influencer`)
      .then(influencer => setInfluencer(influencer))
      .catch(({ data: { error } }) => toastError(error))
  }

  const getAuthentications = () => {
    axiosService.get(`/api/project_manager/authentications/${auth.authentication_id}/authentications`)
      .then(authentications => {
        setAuthentications(authentications)
      })
      .catch(({ data: { error } }) => toastError(error))
  }

  useEffect(() => {
    getInfluencer()
    getAuthentications()
  }, [])

  const authCampTags = auth.tags.reduce((result, tag) => {
    if (tag.campaign_id === +id) {
      result.push(tag)
    }
    return result
  }, [])

  const authTags = auth.tags.reduce((result, tag) => {
    if (tag.campaign_id === null) {
      result.push(tag)
    }
    return result
  }, [])

  const sampleTags = authCampTags.slice(0,2).concat(authTags.slice(0, 2))

  const tagBgColor = (campId, tag) => isUgcCreatorTag(tag) ? '#F5F3FF' : !campId ? '#EFF6FF' : '#FEFCE8'
  const tagTxtColor = (campId, tag) => isUgcCreatorTag(tag) ? '#7C3AED' : !campId ? '#2563EB' : '#EAB308'

  const navigate = useNavigate()
  const [rolledUp, setRolledUp] = useState(true)
  const [selectedAuth, setSelectedAuth] = useState(auth.provider)

  const copyToClipboard = () => {
    navigator.clipboard.writeText(influencer.email)
    .then(() => toastSuccess(`Copied to clipboard: ${influencer.email}`))
    .catch(() => toastError('failed to copy email'))
  }

  const removeInfluencerFromCampaign = e => {
    e.stopPropagation()
    setRemoveInfluencerModalOpen(true)
  }

  return (
    <>
      <Draggable draggableId={`${auth.authentication_id}`} index={index} type="influencer" isDragDisabled={readOnly}>
        {(provided, snapshot) => (
          <div
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            ref={provided.innerRef}
            className={`border-1 shadow rounded-lg mb-3 ${selectedInfluencerAuth?.authentication_id === auth.authentication_id ? 'bg-rb-soft-green dark:bg-rb-green/20 border-rb-green dark:border-rb-green' : 'bg-white dark:bg-rb-dark-mode border-grey-100 dark:border-rb-dark-mode-lighter'} max-h-max ${readOnly ? '' : snapshot.isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
          >
            <div className='flex flex-col flex-grow'>
              <div className="p-3 flex min-w-0 justify-between items-center gap-2 flex-grow">
                <div className="flex min-w-0 flex-1 items-center gap-2">
                  <img src={auth.avatar} className='shrink-0 bg-rb-green border-1 border-grey-500 dark:border-rb-dark-mode-lighter rounded-full w-8 h-8'/>
                  <span className="min-w-0 flex-1 truncate dark:text-white" title={auth.username}>{auth.username}</span>
                  {auth.reachbird_approved && <span className="shrink-0"><ReachbirdApprovedBadge compact /></span>}
                  <button
                    type="button"
                    className="shrink-0"
                    onClick={(e) => {
                      e.stopPropagation()
                      window.open(`/project_manager/authentications/${auth.authentication_id}/influencer/overview`, '_blank', 'noopener,noreferrer')
                    }}
                  >
                    <BoxArrowUpRight className='text-sm text-rb-green dark:text-rb-green'/>
                  </button>
                </div>
                <div className="flex shrink-0 items-center gap-0.5">
                  {!readOnly && <button type="button" className="flex items-center hover:bg-gray-100 dark:hover:bg-rb-dark-mode-lighter rounded-lg p-1 dark:text-white" onClick={removeInfluencerFromCampaign}><Trash3 /></button>}
                  <button type="button" className="flex items-center rounded-lg p-1 dark:text-white" onClick={(e) => { e.stopPropagation(); setRolledUp(!rolledUp) }}>
                    {rolledUp ? <ChevronDown /> : <ChevronUp />}
                  </button>
                </div>
              </div>

              <div className={`border-b-1 ${selectedInfluencerAuth?.authentication_id === auth.authentication_id ? 'border-rb-green dark:border-rb-green' : 'border-grey-100 dark:border-rb-dark-mode-lighter'}`} />
              {
                !rolledUp && (
                  <>
                    <div className="flex items-center p-1">
                      {authentications.map(authentication => (
                        <PlatformButton
                          key={authentication.id}
                          platform={authentication.provider}
                          isSelected={selectedAuth === authentication.provider}
                          onClick={() => setSelectedAuth(authentication.provider)}
                        />
                      ))}
                    </div>
                    <div className={`border-b-1 ${selectedInfluencerAuth?.authentication_id === auth.authentication_id ? 'border-rb-green dark:border-rb-green': 'border-grey-100 dark:border-rb-dark-mode-lighter'}`} />
                  </>
                )
              }

              <div className="flex justify-between p-3 pb-1 items-center">
                <div className='flex items-center'>
                  <p className='mr-1 dark:text-gray-400'>Reach</p>
                  <span className='text-rb-green dark:text-rb-green font-bold'>{formatNumber(authentications?.find(auth => auth.provider === selectedAuth)?.reach || "Unknown", 0)}</span>
                  <div className="flex ml-3 dark:text-gray-400">
                    {(auth.country || auth.main_country) && getUnicodeFlagIcon(auth.country || auth.main_country)}
                    <p className="ml-1">{auth.country || auth.main_country}</p>
                  </div>
                  {auth.rating_count > 0 && (
                    <span title={`Professional ${auth.rating_professional?.toFixed(1)} · Quality ${auth.rating_quality?.toFixed(1)} (${auth.rating_count} ${auth.rating_count === 1 ? 'rating' : 'ratings'})`} className="ml-3 flex items-center gap-1 text-xs dark:text-gray-400">
                      <StarFill className="text-amber-400" />
                      <span>P {auth.rating_professional?.toFixed(1)}</span>
                      <span className="text-rb-lighter-grey">·</span>
                      <span>Q {auth.rating_quality?.toFixed(1)}</span>
                    </span>
                  )}
                </div>
                <div className='flex items-center gap-2'>
                  <SelectionStatusIcon selectionStatus={auth.selection_status} />
                  <div className='flex items-center text-xs gap-1 cursor-pointer dark:text-gray-400'
                    onClick={(e) => {
                      e.stopPropagation()
                      navigate(`/project_manager/campaigns/${id}/influencers/campaign_authentications/${auth.campaign_authentication_id}/pm_thread`)
                    }}
                  >
                    <p>{auth.comments.length + auth.client_comments.length}</p>
                    <ChatDots/>
                  </div>
                </div>
              </div>
            </div>
            {
              !rolledUp && (
                <div>
                  <div className="px-3">
                    <div className="flex justify-between items-center">
                      <p className='text-rb-mat-grey dark:text-gray-400 text-xs'>{influencer.email}</p>
                      {influencer.email && <button type='button' onClick={() => copyToClipboard() } className="dark:text-gray-400">
                        <Copy className='text-rb-mat-grey dark:text-gray-400 text-xs' />
                      </button>}
                    </div>

                    <button
                      className={`my-2 w-full px-4 flex justify-start items-center ${selectedInfluencerAuth?.authentication_id === auth.authentication_id ? 'bg-rb-soft-green dark:bg-rb-green/20 border-rb-green dark:border-rb-green' : 'bg-white dark:bg-rb-dark-mode-lighter border-gray-100 dark:border-rb-dark-mode-lighter'} p-1 border-2 rounded-lg dark:text-white`}
                      onClick={() => navigate(`/project_manager/campaigns/${id}/influencers/campaign_authentications/${auth.campaign_authentication_id}/pm_thread`) }
                    >
                      <Envelope className='text-rb-black dark:text-white mr-2'/>
                      <span>Open Threads</span>
                    </button>
                  </div>
                  <div className="px-3">
                    <div className="my-4 flex gap-2 flex-wrap">
                      {
                        sampleTags.map((tag) => (
                          <span key={tag.id} title={isUgcCreatorTag(tag) ? 'Manually added UGC creator' : undefined} className='px-3 py-1 rounded-full flex items-center gap-1 w-fit' style={{ backgroundColor: tagBgColor(tag.campaign_id, tag) }}>
                            <span style={{ color: tagTxtColor(tag.campaign_id, tag) }}>{isUgcCreatorTag(tag) ? `★ ${tag.name}` : tag.name}</span>
                          </span>
                        ))
                      }
                      {
                        sampleTags.length > 0 && (
                          <span className='px-3 py-1 rounded-full flex items-center gap-1 w-fit bg-gray-100 dark:bg-rb-dark-mode-lighter dark:text-white'>
                            <span>+{(authCampTags.length + authTags.length) - sampleTags.length}</span>
                          </span>
                        )
                      }
                      {!readOnly && (
                        <button
                          className='hover:bg-gray-200 dark:hover:bg-rb-dark-mode-lighter rounded-lg p-2 dark:text-white'
                          type='button'
                          onClick={() => navigate(`/project_manager/campaigns/${id}/influencers/campaign_authentications/${auth.campaign_authentication_id}/tags`) }
                        >
                          <PlusLg className='text-lg' />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )
            }
          </div>
        )}
      </Draggable>
      {removeInfluencerModalOpen && <RemoveInfluencerModal campaignAuthentication={auth} setModalOpen={setRemoveInfluencerModalOpen} modalOpen={removeInfluencerModalOpen}/>}
    </>
  )
})
